// src/App.js
import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedLayout from './components/ProtectedLayout';
import WholesaleRoute from './components/wholesale/WholesaleRoute';
import { Login, Register } from './components/Auth';
import * as Pages from './pages';
import Dashboard from './components/Dashboard';
import './index.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import './config/axios';
import BugReportButton from './components/BugReportButton';

// Define print routes that shouldn't show navigation
const printRoutes = [
  { path: "bol/:bolID/print", component: Pages.BOLPrint },
  { path: "weight-tags/:id/print", component: Pages.WeightTagPrint }
];

// Define main protected routes
const protectedRoutes = [
  { path: "dashboard", component: Dashboard },
  { path: "wines", component: Pages.WineList },
  { path: "wines/:wineID", component: Pages.WineDetail },
  { path: "containers", component: Pages.ContainerList },
  { path: "charts", component: Pages.Charts },
  { path: "bottled-wines", component: Pages.BottledWinesList },
  { path: "bill-of-lading/:bolID", component: Pages.BillOfLading },
  { path: "bill-of-lading", component: Pages.BillOfLading },
  { path: "bill-of-lading-list", component: Pages.BillOfLadingList },
  { path: "companies", component: Pages.CompaniesList },
  { path: "companies/new", component: Pages.CompanyForm },
  { path: "companies/edit/:id", component: Pages.CompanyForm },
  { path: "vineyards", component: Pages.VineyardsList },
  { path: "vineyards/new", component: Pages.VineyardForm },
  { path: "vineyards/edit/:id", component: Pages.VineyardForm },
  { path: "weight-tags", component: Pages.WeightTagList },
  { path: "weight-tags/new", component: Pages.WeightTag },
  { path: "weight-tags/edit/:id", component: Pages.WeightTag },
  { path: "blends", component: Pages.BlendList },
  { path: "blends/new", component: Pages.BlendForm },
  { path: "blends/:blendId", component: Pages.BlendForm },
  { path: "inventory", component: Pages.InventoryMaster },
  { path: "inventory/:logID", component: Pages.InventoryDetail },
  { path: "bottling-plans", component: Pages.BottlingPlansList },
  { path: "bottling-plans/new", component: Pages.BottlingPlanDetail },
  { path: "bottling-plans/:planId", component: Pages.BottlingPlanDetail },
  { path: "workorders", component: Pages.WorkorderList },
  { path: "workorders/new", component: Pages.WorkorderDetail },
  { path: "workorders/:id", component: Pages.WorkorderDetail },
  { path: "grape-contracts", component: Pages.GrapeContracts },
  { path: "grape-contracts/new", component: Pages.GrapeContractDetail },
  { path: "grape-contracts/:id", component: Pages.GrapeContractDetail },
  { path: "harvest-planning", component: Pages.HarvestPlanning },
  { path: "materials", component: Pages.MaterialsList },
  { path: "materials/new", component: Pages.MaterialsList },
  { path: "materials/edit/:id", component: Pages.MaterialsList },
  { path: "material-inventory", component: Pages.MaterialsInventoryList },
  { path: "material-inventory/:logID", component: Pages.MaterialsInventory },
  { path: "material-categories", component: Pages.MaterialsList },
  { path: "facility", component: Pages.MqttDashboard },
  { path: "reports/tax-classes", component: Pages.TaxReport },
  { path: "settings", component: Pages.Settings },
  // New Tasting Room Routes
  { path: "tasting-room", component: Pages.TastingRoomInventoryList},
  //{ path: "tasting-room/locations", component: Pages.TastingRoomLocations },
  { path: "tasting-room/categories", component: Pages.TastingRoomInventoryList },
  { path: "tasting-room/inventory", component: Pages.TastingRoomInventoryList },
  { path: "tasting-room/inventory/new", component: Pages.TastingRoomInventoryDetail },
  { path: "tasting-room/inventory/:inventoryID", component: Pages.TastingRoomInventoryDetail },
  { path: "tasting-room/inventory-logs", component: Pages.TastingRoomInventoryList },
  { path: "tasting-room/inventory-logs/new", component: Pages.TastingRoomInventoryDetail },
  { path: "tasting-room/inventory-logs/:logID", component: Pages.TastingRoomInventoryDetail },
  { path: "bug-reports", component: Pages.BugReportManagement } // Bug Report Management
];
const wholesaleRoutes = [
  { path: "wholesale/catalog", component: Pages.Catalog },
  { path: "wholesale/checkout", component: Pages.Checkout },
  { path: "wholesale/order-confirmation", component: Pages.OrderConfirmation },
  { path: "wholesale/orders", component: Pages.Orders },
  { path: "wholesale/orders/:orderId", component: Pages.OrderDetail }
];

const App = () => {
  return (
    
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/wholesale/register" element={<Pages.Register />} />

          {/* Print Routes - Protected but no navigation */}
          <Route element={<ProtectedLayout hideNav={true} />}>
            {printRoutes.map(({ path, component: Component }) => (
              <Route key={path} path={path} element={<Component />} />
            ))}
          </Route>
          
          {/* Wholesale Routes */}
          <Route element={<WholesaleRoute />}>
            {wholesaleRoutes.map(({ path, component: Component }) => (
              <Route key={path} path={path} element={<Component />} />
            ))}
          </Route>

          {/* Main Protected Routes with Navigation */}
          <Route element={<ProtectedLayout />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            {protectedRoutes.map(({ path, component: Component }) => (
              <Route key={path} path={path} element={<Component />} />
            ))}
          </Route>

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <BugReportButton />
      </Router>
    </AuthProvider>
  );
};

export default App;